import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldAlert, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const redirectFor = (role) => (role === "admin" ? "/dashboard" : role === "client" ? "/client" : "/employee");

const MagicLogin = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagic } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setError("This sign-in link is missing its token."); return; }
    (async () => {
      try {
        const res = await loginWithMagic(token);
        if (res.needs_password_setup) navigate("/set-password", { replace: true });
        else navigate(redirectFor(res.user.role), { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || "This sign-in link is invalid or has expired.");
      }
    })();
  }, [params, loginWithMagic, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50/60 px-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-sm">
        {!error ? (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#2f2b8f]" />
            <h1 className="mt-5 text-xl font-bold text-[#1a1a2e]">Signing you in…</h1>
            <p className="mt-1 text-sm text-slate-500">Hang tight while we verify your secure link.</p>
          </>
        ) : (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50"><ShieldAlert className="h-7 w-7 text-rose-500" /></div>
            <h1 className="mt-5 text-xl font-bold text-[#1a1a2e]">Link expired or invalid</h1>
            <p className="mt-1 text-sm text-slate-500" data-testid="magic-error">{error}</p>
            <Link to="/auth/client"><Button className="group mt-6 w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">Go to sign in<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Button></Link>
            <p className="mt-3 text-xs text-slate-400">On the sign-in page, choose "Email me a sign-in code" to get a fresh link or code.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLogin;
